import type { HeadsUp, ToolOutput } from '@hugo/core'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
  BellRingIcon,
  CheckIcon,
  ClockAlertIcon,
  CopyIcon,
  MegaphoneIcon,
  SendIcon,
  TreePalmIcon,
  XIcon,
} from 'lucide-react'
import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { toast } from '@/components/ui/toast'
import { copyText } from '@/lib/clipboard'
import { formatDay, sourceLabel } from '@/lib/format'
import { queryKeys, useRunTool } from '@/lib/items'
import { headsUpReason, headsUpTitle, newDateNote, reminderNote, userToday } from '@/lib/plan'
import { useUserNow } from '@/lib/session'
import { attention } from '@/lib/styles'
import { cn } from '@/lib/utils'
import { Parts } from './parts'
import { useWorkspace } from './workspace'

type HeadsUps = ToolOutput<'headsups.list'>['headsUps']

/** Full-width and tall enough for a thumb on phones; a normal button from `sm` up. */
const THUMB = 'h-10 w-full sm:h-8 sm:w-auto'

const isReminder = (headsUp: Pick<HeadsUp, 'kind'>) => headsUp.kind === 'waiting_followup'

/**
 * Act on a heads-up or reminder: put it in the Outbox (reply channel; approval as always), or
 * mark it as handled: "I told them" (a late promise's due date moves to the date told), "I
 * reminded them" (the next follow-up date is set) or "Not needed". It leaves the Outbox's list
 * right away and comes back if the call fails; one already on its way is not acted on twice.
 */
function useHeadsUpActions() {
  const queryClient = useQueryClient()
  const { run } = useRunTool()
  const navigate = useNavigate()
  const { openDetails } = useWorkspace()
  const inflight = useRef(new Set<string>())

  return useMemo(() => {
    const settle = async <T,>(episode: string, call: () => Promise<T | null>) => {
      if (inflight.current.has(episode)) return null
      inflight.current.add(episode)
      try {
        await queryClient.cancelQueries({ queryKey: queryKeys.headsUps })
        const snapshot = queryClient.getQueryData<HeadsUps>(queryKeys.headsUps)
        queryClient.setQueryData<HeadsUps>(queryKeys.headsUps, (old) =>
          old?.filter((h) => h.episode !== episode),
        )
        const result = await call()
        if (result === null && snapshot) queryClient.setQueryData(queryKeys.headsUps, snapshot)
        return result
      } finally {
        inflight.current.delete(episode)
      }
    }
    const openOutbox = () => {
      openDetails(null)
      void navigate({ to: '/outbox' })
    }
    return {
      /** Reply channel only. `newDate`: another date than hugo's for a late promise. */
      draft: (headsUp: HeadsUp, newDate: string | null) =>
        settle(headsUp.episode, () =>
          run(
            'headsup.draft',
            { episode: headsUp.episode, ...(newDate ? { newDate } : {}) },
            {
              done: isReminder(headsUp)
                ? 'Reminder waiting for approval'
                : 'Heads-up waiting for approval',
              doneAction: { label: 'Open Outbox', onClick: openOutbox },
              failed: isReminder(headsUp)
                ? 'Could not add the reminder'
                : 'Could not add the heads-up',
            },
          ),
        ),
      /**
       * "I told them": for a late promise the due date moves to `date`. "I reminded them": the
       * next follow-up is hugo's (the user's reminder interval on).
       */
      told: (headsUp: HeadsUp, date: string | null, thisYear: number) => {
        const next = isReminder(headsUp)
          ? headsUp.newDate
          : headsUp.kind === 'owe_late'
            ? date
            : null
        const setDue = next ? { date: next } : null
        const done = !setDue
          ? isReminder(headsUp)
            ? 'Reminder done'
            : 'Heads-up done'
          : isReminder(headsUp)
            ? `Next follow-up ${formatDay(setDue.date, thisYear)}`
            : `Due date moved to ${formatDay(setDue.date, thisYear)}`
        return settle(headsUp.episode, () =>
          run(
            'headsup.dismiss',
            { episode: headsUp.episode, ...(setDue ? { setDue } : {}) },
            { done, failed: 'Could not save' },
          ),
        )
      },
      notNeeded: (headsUp: HeadsUp) =>
        settle(headsUp.episode, () =>
          run(
            'headsup.dismiss',
            { episode: headsUp.episode },
            isReminder(headsUp)
              ? { done: 'Reminder dismissed', failed: 'Could not dismiss the reminder' }
              : { done: 'Heads-up dismissed', failed: 'Could not dismiss the heads-up' },
          ),
        ),
    }
  }, [queryClient, run, navigate, openDetails])
}

/**
 * The text to send, as the server wrote it (never edited here). In the Outbox it shows three
 * lines until expanded.
 */
function HeadsUpText({ text, collapsible }: { text: string; collapsible: boolean }) {
  const id = useId()
  const ref = useRef<HTMLParagraphElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [overflows, setOverflows] = useState(false)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el || !collapsible || expanded || text === '') return
    const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 1)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [collapsible, expanded, text])
  return (
    <div className="flex flex-col items-start gap-1">
      {/* The border and padding sit around the clamped text: a clamp clips at its own padding
          edge, so padding on the same element would show part of the fourth line. */}
      <div className="w-full rounded-lg border bg-background p-3 text-sm">
        <p
          id={id}
          ref={ref}
          data-testid="headsup-text"
          className={cn(
            'break-words whitespace-pre-wrap',
            collapsible && !expanded && 'line-clamp-3',
          )}
        >
          {text}
        </p>
      </div>
      {collapsible && (overflows || expanded) ? (
        <Button
          variant="link"
          size="sm"
          className="h-8 px-0 max-sm:h-10"
          aria-expanded={expanded}
          aria-controls={id}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? 'Show less' : 'Show all'}
        </Button>
      ) : null}
    </div>
  )
}

/**
 * The same actions in the details and in the Outbox. A late promise gets a "New date" (hugo's
 * date until changed): for a reply the server writes it into the message, for a copied text the
 * user changes it there. Reply: "Add to Outbox". Copy: "Copy text", then "I told them" ("I
 * reminded them" for a reminder). Always "Not needed".
 */
function HeadsUpActions({
  headsUp,
  today,
  thisYear,
}: {
  headsUp: HeadsUp
  today: string
  thisYear: number
}) {
  const actions = useHeadsUpActions()
  const dateId = useId()
  /**
   * The date the user picked, if any. Until then the field shows hugo's suggestion, which moves
   * when other promises change (told someone in another tab, more work, time off): the date
   * saved must be the one the text says. A cleared field means hugo's date again.
   */
  const [picked, setPicked] = useState<string | null>(null)
  const date = picked ?? headsUp.newDate ?? ''
  const [busy, setBusy] = useState<'draft' | 'told' | 'dismiss' | null>(null)
  const [copied, setCopied] = useState(false)
  const late = headsUp.kind === 'owe_late' && headsUp.newDate !== null
  const chosen = picked ?? headsUp.newDate
  const changed = late && chosen !== null && chosen !== headsUp.newDate
  const tooEarly = late && chosen !== null && chosen < today

  const act = async (which: NonNullable<typeof busy>, action: () => Promise<unknown>) => {
    setBusy(which)
    await action()
    setBusy(null)
  }
  const copy = async () => {
    if (await copyText(headsUp.text)) {
      setCopied(true)
      toast.add({ title: 'Copied', type: 'success', timeout: 2500 })
    } else {
      toast.add({
        title: 'Could not copy',
        description: 'Select the text and copy it yourself.',
        type: 'error',
      })
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {late && headsUp.newDate ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={dateId}>New date</Label>
          <Input
            id={dateId}
            type="date"
            className="w-full sm:w-44"
            value={date}
            min={today}
            aria-invalid={tooEarly || undefined}
            aria-describedby={changed || tooEarly ? `${dateId}-note` : undefined}
            onChange={(event) => setPicked(event.target.value || null)}
          />
          {tooEarly ? (
            <p id={`${dateId}-note`} role="alert" className="text-sm text-destructive">
              Pick today or a later day.
            </p>
          ) : changed && chosen ? (
            <p
              id={`${dateId}-note`}
              className="text-sm text-muted-foreground"
              data-testid="headsup-date-note"
            >
              {newDateNote(headsUp.channel, headsUp.newDate, chosen, thisYear)}
            </p>
          ) : null}
        </div>
      ) : null}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {headsUp.channel === 'reply' ? (
          <Button
            className={THUMB}
            disabled={busy !== null || tooEarly}
            onClick={() => void act('draft', () => actions.draft(headsUp, changed ? chosen : null))}
          >
            {busy === 'draft' ? <Spinner aria-hidden="true" /> : <SendIcon aria-hidden="true" />}
            Add to Outbox
          </Button>
        ) : (
          <>
            <Button
              variant={copied ? 'outline' : 'default'}
              className={THUMB}
              disabled={busy !== null}
              onClick={() => void copy()}
            >
              <CopyIcon aria-hidden="true" />
              Copy text
            </Button>
            <Button
              variant={copied ? 'default' : 'outline'}
              className={THUMB}
              disabled={busy !== null || tooEarly}
              onClick={() =>
                void act('told', () => actions.told(headsUp, late ? chosen : null, thisYear))
              }
            >
              {busy === 'told' ? <Spinner aria-hidden="true" /> : <CheckIcon aria-hidden="true" />}
              {isReminder(headsUp) ? 'I reminded them' : 'I told them'}
            </Button>
          </>
        )}
        <Button
          variant="ghost"
          className={THUMB}
          disabled={busy !== null}
          onClick={() => void act('dismiss', () => actions.notNeeded(headsUp))}
        >
          {busy === 'dismiss' ? <Spinner aria-hidden="true" /> : <XIcon aria-hidden="true" />}
          Not needed
        </Button>
      </div>
    </div>
  )
}

const REASON_ICONS = {
  owe_late: ClockAlertIcon,
  waiting_away: TreePalmIcon,
  waiting_followup: BellRingIcon,
} as const

function ReasonIcon({ kind }: { kind: HeadsUp['kind'] }) {
  const Icon = REASON_ICONS[kind]
  return <Icon aria-hidden="true" className={cn('mt-0.5 size-4 shrink-0', attention.icon)} />
}

/** "If Marco doesn't reply, hugo suggests another reminder on Mon 26 Oct." (reminders only). */
function NextReminder({ headsUp, thisYear }: { headsUp: HeadsUp; thisYear: number }) {
  const note = isReminder(headsUp) ? reminderNote(headsUp, thisYear) : null
  if (!note) return null
  return (
    <p className="text-xs text-muted-foreground" data-testid="reminder-next">
      {note}
    </p>
  )
}

/**
 * In an item's details: "Tell Anna" (or "Remind Marco"), why, the text hugo wrote, and what to do
 * with it. Keyed by episode where it is used, so a new due date or time off starts over.
 */
export function HeadsUpPanel({ headsUp, timeZone }: { headsUp: HeadsUp; timeZone: string }) {
  const now = useUserNow(60_000)
  const { today, year } = userToday(now, timeZone)
  const titleId = useId()
  return (
    <section
      aria-labelledby={titleId}
      className="flex flex-col gap-3 rounded-lg border p-3"
      data-testid="headsup-panel"
      data-kind={headsUp.kind}
      data-channel={headsUp.channel}
    >
      <div className="flex flex-col gap-1">
        <h3 id={titleId} className="flex items-center gap-1.5 text-sm font-medium">
          {isReminder(headsUp) ? (
            <BellRingIcon aria-hidden="true" className={cn('size-4', attention.icon)} />
          ) : (
            <MegaphoneIcon aria-hidden="true" className={cn('size-4', attention.icon)} />
          )}
          {headsUpTitle(headsUp)}
        </h3>
        <p className="text-sm text-muted-foreground" data-testid="headsup-reason">
          <Parts text={headsUpReason(headsUp, today, year)} />
        </p>
      </div>
      <HeadsUpText text={headsUp.text} collapsible={false} />
      <p className="text-xs text-muted-foreground">
        {headsUp.channel === 'reply'
          ? 'It waits in the Outbox until you approve it.'
          : 'hugo has no thread to answer in: copy the text and send it yourself.'}
      </p>
      <NextReminder headsUp={headsUp} thisYear={year} />
      <HeadsUpActions headsUp={headsUp} today={today} thisYear={year} />
    </section>
  )
}

/**
 * In the Outbox's "Heads-ups to send" (and reminders): the person, the item (opens its details),
 * why, the text.
 */
export function HeadsUpCard({
  headsUp,
  today,
  thisYear,
}: {
  headsUp: HeadsUp
  today: string
  thisYear: number
}) {
  const { openDetails } = useWorkspace()
  const personId = useId()
  return (
    <article
      aria-labelledby={personId}
      className="flex flex-col gap-3 p-4"
      data-testid="headsup"
      data-episode={headsUp.episode}
      data-kind={headsUp.kind}
      data-channel={headsUp.channel}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col items-start gap-0.5">
          <h3 id={personId} className="font-medium break-words" data-testid="headsup-person">
            {headsUp.person ?? headsUpTitle(headsUp)}
          </h3>
          <button
            type="button"
            className="max-w-full rounded-sm text-left text-sm break-words text-muted-foreground underline-offset-4 outline-none hover:underline focus-visible:ring-3 focus-visible:ring-ring/50"
            onClick={() => openDetails(headsUp.itemId)}
          >
            {headsUp.title}
          </button>
        </div>
        <Badge variant="outline" className="shrink-0 font-normal">
          {headsUp.channel === 'reply'
            ? `Reply in ${sourceLabel(headsUp.source)}`
            : 'Copy and send'}
        </Badge>
      </div>
      <p className="flex items-start gap-1.5 text-sm" data-testid="headsup-reason">
        <ReasonIcon kind={headsUp.kind} />
        <span className="min-w-0 break-words">
          <Parts text={headsUpReason(headsUp, today, thisYear)} />
        </span>
      </p>
      <HeadsUpText text={headsUp.text} collapsible />
      <NextReminder headsUp={headsUp} thisYear={thisYear} />
      <HeadsUpActions headsUp={headsUp} today={today} thisYear={thisYear} />
    </article>
  )
}
